import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import { RowActions } from "@/shared/ui/RowActions";
import type { StaffDTO } from "../../domain/contracts";

type StaffTableProps = {
  data: StaffDTO[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  onEdit: (row: StaffDTO) => void;
  onDelete: (id: string) => void;
};

export function StaffTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  onEdit,
  onDelete,
}: StaffTableProps) {
  const columns: TanstackReactTable.ColumnDef<StaffDTO>[] = [
    createSortableColumn("displayName", "ຊື່"),
    {
      id: "performerName",
      header: "ນັກສະແດງ",
      cell: ({ row }) => row.original.performerName || "—",
    },
    {
      id: "defaultRoleLabel",
      header: "ບົດບາດ",
      cell: ({ row }) => row.original.defaultRoleLabel || "—",
    },
    {
      id: "phone",
      header: "ເບີໂທ",
      cell: ({ row }) => row.original.phone || "—",
    },
    {
      id: "bank",
      header: "ບັນຊີ",
      cell: ({ row }) => {
        const { bankAccount, bankName } = row.original;
        if (!bankAccount) return "—";
        return `${bankName ?? ""} · ${bankAccount}`.trim();
      },
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) =>
        row.original.status === "active" ? (
          <Badge variant="outline">ໃຊ້ງານ</Badge>
        ) : (
          <Badge variant="secondary">ປິດໃຊ້</Badge>
        ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ແກ້ໄຂ",
              icon: <EditIcon className="size-4" />,
              onClick: () => onEdit(row.original),
            },
            {
              label: "ລຶບ",
              icon: <TrashIcon className="size-4" />,
              variant: "destructive",
              onClick: async () => {
                const ok = await confirm({
                  title: "ລຶບ Staff",
                  description: "ຢືນຢັນລຶບ Staff ນີ້?",
                  actionText: "ລຶບ",
                  ActionProps: { variant: "destructive" },
                });
                if (ok) onDelete(row.original.id);
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable<StaffDTO, unknown>
      noDataMessage="ບໍ່ພົບ Staff"
      data={data}
      columns={columns}
      isLoading={isLoading}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
